import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type TCapManageTranslator = TranslateFunction<I18Namespaces.CAP_MANAGE>;

/**
 * The human sentence for a row's `transactionError`, shared by every surface that has to explain
 * why a transaction is invalid — the grid's warning column and the transaction detail page.
 *
 * Codes come from two places: TRANSACTION_VALIDATION_ERROR on the backend, and the SQL validation
 * chain, which additionally emits PAIR_LEG_MISSING and NEGATIVE_SHARE_CAPITAL. An unrecognised code
 * falls through to itself rather than to silence — a code with no phrase yet should still name
 * something the user can quote to support.
 */
export const getTransactionErrorText = (
	transactionError: string | null | undefined,
	tCapManage: TCapManageTranslator
): string | null => {
	if (!transactionError) {
		return null;
	}

	switch (transactionError) {
		case 'NO_SOURCE_BALANCE':
			return tCapManage('transactions.table.columns.errors.no_source_balance');
		case 'OVERSOLD':
			return tCapManage('transactions.table.columns.errors.oversold');
		case 'SOURCE_ENTITY_REQUIRED':
			return tCapManage('transactions.table.columns.errors.source_entity_required');
		case 'DESTINATION_ENTITY_REQUIRED':
			return tCapManage('transactions.table.columns.errors.destination_entity_required');
		case 'SOURCE_DESTINATION_SAME':
			return tCapManage(
				'transactions.table.columns.errors.source_destination_must_be_different'
			);
		case 'PAIR_LEG_MISSING':
			return tCapManage('transactions.table.columns.errors.pair_leg_missing');
		case 'SOURCE_MUST_BE_ENTITY':
			return tCapManage('transactions.table.columns.errors.source_must_be_entity');
		case 'DESTINATION_MUST_BE_ENTITY':
			return tCapManage('transactions.table.columns.errors.destination_must_be_entity');
		case 'INSUFFICIENT_DATA':
			return tCapManage('transactions.table.columns.errors.insufficient_data');
		case 'TRANSACTION_TYPE_REQUIRED':
			return tCapManage('transactions.table.columns.errors.transaction_type_required');
		case 'NEGATIVE_SHARE_CAPITAL':
			return tCapManage('transactions.table.columns.errors.negative_share_capital');
		default:
			return transactionError;
	}
};
